import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import type {
  GetAvailableValuesResult,
  Country,
  Region,
  Destination,
  Hotel,
  ProductGroup,
} from '@/types/schemas';
import type { DurationOption } from '@/types/SearchBar';
import { getAvailableValues } from '@/services/api/player';
import { PlayerApiError } from '@/types/errors';
import { PLAYER_API_CONFIG } from '@/constants/player/config';
import { $t } from '@/composables/translations/useTranslate';
import { IS_DEV_GATEWAY } from '@/constants/environment';

const CACHE_TTL = PLAYER_API_CONFIG.CACHE.AVAILABLE_VALUES_TTL;

/**
 * Store for Player API Available Values
 *
 * Features:
 * - Computed getters for easy data access
 * - Select options for form components
 *
 * Usage:
 * ```ts
 * const store = usePlayerAvailableValuesStore();
 * await store.fetchAvailableValues(); // Fetches from API
 * const countries = store.countries; // Reactive array
 * ```
 *
 * Caching is disabled for local dev and the staging deployment (`IS_DEV_GATEWAY`):
 * an in-memory cache-guard skips refetching while data is fresh (1h TTL), and
 * localStorage persistence keeps it across reloads — only on production. In
 * dev/staging data is always refetched and nothing is persisted.
 */
export const usePlayerAvailableValuesStore = defineStore(
  'playerAvailableValues',
  () => {
    const loading = ref(false);
    const error = ref<string | null>(null);
    const lastFetched = ref<Date | null>(null);
    const data = ref<GetAvailableValuesResult | null>(null);

    // Getters
    const countries = computed<Country[]>(() => data.value?.Countries?.Country || []);
    const regions = computed<Region[]>(() => data.value?.Regions?.Region || []);
    const destinations = computed<Destination[]>(() => data.value?.Destinations?.Destination || []);
    const hotels = computed<Hotel[]>(() => data.value?.Hotels?.Hotel || []);
    const productGroups = computed<ProductGroup[]>(
      () => data.value?.ProductGroups?.ProductGroup || []
    );

    // Map ISO country code → top-level ProductGroup Id (from autoTree root's direct children).
    const countryGroupIdMap = computed<Record<string, number>>(() => {
      const topLevelGroups = productGroups.value.flatMap((root) => {
        const inner = root.ProductGroups?.ProductGroup;
        if (!inner) return [];
        return Array.isArray(inner) ? inner : [inner];
      });
      const nameToId = new Map(topLevelGroups.map((g) => [g.Name, g.Id]));

      const map: Record<string, number> = {};
      for (const country of countries.value) {
        const id = nameToId.get(country.Name);
        if (id !== undefined) {
          map[country.Code] = id;
        }
      }
      return map;
    });
    const boardTypes = computed<string[]>(() => data.value?.BoardTypes?.BoardType || []);
    const roomTypes = computed<string[]>(() => data.value?.RoomTypes?.RoomType || []);
    const accommodationProperties = computed<string[]>(
      () => data.value?.AccommodationProperties?.AccommodationProperty || []
    );
    const durations = computed<number[]>(() => data.value?.Durations?.Duration || []);
    const dateRange = computed<{
      min: string | null;
      max: string | null;
    }>(() => ({
      min: data.value?.MinDate || null,
      max: data.value?.MaxDate || null,
    }));

    // Convert ISO datetime to YYYY-MM-DD format
    const formatDateForInput = (isoDate: string | null): string | null => {
      if (!isoDate) return null;
      try {
        return isoDate.split('T')[0];
      } catch {
        return null;
      }
    };

    // Date range formatted for HTML date inputs (YYYY-MM-DD)
    const dateRangeForInput = computed<{
      min: string | null;
      max: string | null;
    }>(() => ({
      min: formatDateForInput(dateRange.value.min),
      max: formatDateForInput(dateRange.value.max),
    }));

    // Getters - Status
    const isSuccess = computed<boolean>(() => data.value?.Success === true);
    const hasData = computed<boolean>(() => data.value !== null);
    const isStale = computed<boolean>(() => {
      if (!lastFetched.value) return true;
      const staleTime = new Date(Date.now() - CACHE_TTL);
      return lastFetched.value < staleTime;
    });

    // Getters - Options for Selects
    const countryOptions = computed(() =>
      countries.value.map((country) => ({
        value: country.Code,
        label: `${country.Name} (${country.Code})`,
      }))
    );

    const regionOptions = computed(() =>
      regions.value.map((region) => ({
        value: region.Code,
        label: `${region.Name} (${region.Code})`,
      }))
    );

    const destinationOptions = computed(() =>
      destinations.value.map((dest) => ({
        value: dest.Code,
        label: `${dest.Name} (${dest.Code})`,
      }))
    );

    const hotelOptions = computed(() =>
      hotels.value.map((hotel) => ({
        value: hotel.ProductCode,
        label: `${hotel.Name} (${hotel.ProductCode})`,
      }))
    );

    const boardTypeOptions = computed(() =>
      boardTypes.value.map((type) => ({
        value: type,
        label: type,
      }))
    );

    const roomTypeOptions = computed(() =>
      roomTypes.value.map((type) => ({
        value: type,
        label: type,
      }))
    );

    const durationOptions = computed<DurationOption[]>(() => {
      const options: DurationOption[] = [
        { value: 0, label: $t('ibe.duration.exact', 'Exact'), type: 'exact' },
        { value: 3, label: $t('ibe.duration.3-days', '3 days'), type: 'preset' },
        { value: 5, label: $t('ibe.duration.5-days', '5 days'), type: 'preset' },
        { value: 7, label: $t('ibe.duration.1-week', '1 week'), type: 'preset' },
        { value: 10, label: $t('ibe.duration.10-days', '10 days'), type: 'preset' },
        { value: 14, label: $t('ibe.duration.2-weeks', '2 weeks'), type: 'preset' },
        { value: 21, label: $t('ibe.duration.3-weeks', '3 weeks'), type: 'preset' },
      ];

      const existingValues = new Set(options.map((opt) => opt.value));
      const sortedDurations = [...durations.value].sort((a, b) => a - b);

      for (const duration of sortedDurations) {
        if (!existingValues.has(duration)) {
          const label =
            duration % 7 === 0
              ? `${duration / 7} ${duration === 7 ? 'week' : 'weeks'}`
              : `${duration} ${duration === 1 ? 'day' : 'days'}`;

          options.push({
            value: duration,
            label,
            type: 'preset',
          });
        }
      }

      return options;
    });

    // Actions
    async function fetchAvailableValues(force = false): Promise<void> {
      if (!force && !IS_DEV_GATEWAY && hasData.value && !isStale.value) {
        return;
      }

      loading.value = true;
      error.value = null;

      try {
        const { result } = await getAvailableValues();

        if (!result) {
          throw new Error('No result returned from API');
        }

        if (!result.Success) {
          const errorMessages =
            result.ProcessMessages?.ProcessMessage?.filter(
              (msg) => msg.MessageType === 'Error' || msg.MessageType === 'ConfigurationError'
            )
              .map((msg) => msg.Message)
              .filter(Boolean) || [];

          throw new Error(errorMessages.join(', ') || 'API returned Success=false');
        }

        data.value = result;
        lastFetched.value = new Date();
      } catch (err) {
        if (err instanceof PlayerApiError) {
          error.value = `${err.code}: ${err.message}`;
        } else if (err instanceof Error) {
          error.value = err.message;
        } else {
          error.value = 'Unknown error occurred';
        }
        throw err;
      } finally {
        loading.value = false;
      }
    }

    function clear(): void {
      data.value = null;
      lastFetched.value = null;
      error.value = null;
    }

    function invalidateCache(): void {
      lastFetched.value = null;
    }

    return {
      // State
      loading,
      error,
      lastFetched,
      data,

      // Getters - Data Arrays
      countries,
      regions,
      destinations,
      hotels,
      productGroups,
      countryGroupIdMap,
      boardTypes,
      roomTypes,
      accommodationProperties,
      durations,
      dateRange,
      dateRangeForInput,

      // Getters - Status
      isSuccess,
      hasData,
      isStale,

      // Getters - Options for Selects
      countryOptions,
      regionOptions,
      destinationOptions,
      hotelOptions,
      boardTypeOptions,
      roomTypeOptions,
      durationOptions,

      // Actions
      fetchAvailableValues,
      clear,
      invalidateCache,
    };
  },
  {
    persist: !IS_DEV_GATEWAY && {
      key: 'bewotec_cache_available_values',
      pick: ['data', 'lastFetched'],
      serializer: {
        serialize: JSON.stringify,
        deserialize: (raw: string) => {
          const parsed = JSON.parse(raw);
          if (parsed?.lastFetched) parsed.lastFetched = new Date(parsed.lastFetched);
          return parsed;
        },
      },
    },
  }
);
